import type { ContentPost } from 'api/new-analytics/types';
import dayjs from 'dayjs';
import NumberUtil from 'utils/number';
import TimeUtils from 'app/utils/time';
import { openBlank } from 'utils/url';
import type {
  ContentPostTableRow,
  MetricCellData,
} from '../types/contentTableRow';
import type { ContentMetricKey } from './contentTableColumns';
import { getContentPostRowPostType } from './contentPostType';
import { getContentPostPreviewImageUrl } from './getContentPostPreviewImageUrl';
import { NOT_AVAILABLE } from '../../performance/utils/getPerformanceMetricDisplay';

function formatMetric(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) {
    return NOT_AVAILABLE;
  }
  return NumberUtil.compact(n);
}

function formatPercent(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) {
    return NOT_AVAILABLE;
  }
  return `${NumberUtil.format(n, { maximumFractionDigits: 1 })}%`;
}

function toRaw(n: number | undefined): number | undefined {
  if (n == null || !Number.isFinite(n)) {
    return undefined;
  }
  return n;
}

const METRIC_KEYS: ContentMetricKey[] = [
  'engagement',
  'engagementRate',
  'impressions',
  'reach',
  'reactions',
  'plays',
  'videoViews',
  'shares',
  'clicks',
  'views',
  'likes',
  'comments',
  'saves',
  'profileVisits',
  'follows',
  'avgWatchTime',
  'totalWatchTime',
  'replies',
  'storyTapsForward',
  'storyTapsBack',
  'storyExits',
  'storySwipeForward',
];

function buildMetricCell(
  key: ContentMetricKey,
  m: ContentPost['metrics'],
): MetricCellData {
  switch (key) {
    case 'engagement': {
      const engagement = m.engagements ?? m.engagement;
      return {
        display: formatMetric(engagement),
        raw: toRaw(engagement),
      };
    }
    case 'engagementRate':
      return {
        display: formatPercent(m.engagement_rate),
        raw: toRaw(m.engagement_rate),
      };
    case 'impressions':
      return {
        display: formatMetric(m.impressions),
        raw: toRaw(m.impressions),
      };
    case 'reach':
      return { display: formatMetric(m.reach), raw: toRaw(m.reach) };
    case 'reactions':
      return {
        display: formatMetric(m.reactions),
        raw: toRaw(m.reactions),
      };
    case 'plays':
      return {
        display: formatMetric(m.total_video_plays),
        raw: toRaw(m.total_video_plays),
      };
    case 'videoViews':
      return {
        display: formatMetric(m.video_views),
        raw: toRaw(m.video_views),
      };
    case 'shares':
      return { display: formatMetric(m.shares), raw: toRaw(m.shares) };
    case 'clicks':
      return { display: formatMetric(m.clicks), raw: toRaw(m.clicks) };
    case 'views':
      return { display: formatMetric(m.views), raw: toRaw(m.views) };
    case 'likes':
      return { display: formatMetric(m.likes), raw: toRaw(m.likes) };
    case 'comments':
      return { display: formatMetric(m.comments), raw: toRaw(m.comments) };
    case 'saves':
      return { display: formatMetric(m.saved), raw: toRaw(m.saved) };
    case 'profileVisits':
      return {
        display: formatMetric(m.profile_views),
        raw: toRaw(m.profile_views),
      };
    case 'follows':
      return {
        display: formatMetric(m.followers_gained),
        raw: toRaw(m.followers_gained),
      };
    case 'avgWatchTime':
      return {
        display:
          m.video_avg_time_watched != null
            ? TimeUtils.formatMilliSeconds(m.video_avg_time_watched)
            : '—',
        raw: toRaw(m.video_avg_time_watched),
      };
    case 'totalWatchTime':
      return {
        display:
          m.video_time_watched != null
            ? TimeUtils.formatMilliSeconds(m.video_time_watched)
            : '—',
        raw: toRaw(m.video_time_watched),
      };
    case 'replies':
      return { display: formatMetric(m.replies), raw: toRaw(m.replies) };
    case 'storyTapsForward':
      return {
        display: formatMetric(m.story_taps_forward),
        raw: toRaw(m.story_taps_forward),
      };
    case 'storyTapsBack':
      return {
        display: formatMetric(m.story_taps_back),
        raw: toRaw(m.story_taps_back),
      };
    case 'storyExits':
      return {
        display: formatMetric(m.story_exits),
        raw: toRaw(m.story_exits),
      };
    case 'storySwipeForward':
      return {
        display: formatMetric(m.story_swipe_forward),
        raw: toRaw(m.story_swipe_forward),
      };
    default:
      return { display: '—' };
  }
}

export function getContentPostListKey(post: ContentPost): string {
  if (post.post_id != null) {
    return String(post.post_id);
  }
  return `${post.page_id}-${post.published_at}`;
}

export function getContentPostRowId(post: ContentPost): string {
  return getContentPostListKey(post);
}

export function mapContentPostToRow(post: ContentPost): ContentPostTableRow {
  const m = post.metrics;

  const metrics = {} as Record<ContentMetricKey, MetricCellData>;
  for (const key of METRIC_KEYS) {
    metrics[key] = buildMetricCell(key, m);
  }

  const permalink = post.permalink?.trim();
  const onOpenPost =
    permalink !== undefined && permalink.length > 0
      ? () => openBlank(permalink)
      : undefined;

  const previewText = post.content?.trim() || post.preview?.text?.trim() || '';

  const previewImageUrl = getContentPostPreviewImageUrl(post);
  const postType = getContentPostRowPostType(post);

  return {
    id: getContentPostRowId(post),
    entityId: post.entity_id,
    previewText,
    previewImageUrl,
    postType,
    dateLabel: dayjs(post.published_at).format('MMM D'),
    metrics,
    onOpenPost,
  };
}
